// src/shared/composables/useCookieConsent.ts
//
// LGPD-compliant cookie / analytics consent (SeuID).
//
// Design (see the architecture decision in the project memory):
//   1. NOTHING from Google is loaded on page render. gtag.js is injected at
//      RUNTIME, and only after the visitor explicitly clicks "Aceitar".
//   2. The choice (granted | denied) is persisted in localStorage together with
//      a timestamp and a policy version, so the banner does not reappear once a
//      decision is made — until the version bumps or the visitor reopens it.
//   3. Refusing is exactly as easy as accepting (art. 7º + art. 8º, §4º LGPD):
//      one click, and it loads nothing.
//   4. The choice is revocable at any time via `openBanner()` (wired to the
//      "Cookies" affordance on the Privacy page): it clears the stored decision
//      and shows the banner again.
//   5. The injected <script> carries the per-response CSP nonce (read from the
//      server bootstrap payload — the same nonce the site already stamps on its
//      own script tags), so it is accepted by the strict `script-src 'nonce-…'`
//      policy WITHOUT ever widening script-src.

import { computed, readonly, ref } from 'vue';

import { getBootstrapValue } from '@/services/bootstrap.service';

/**
 * Bump when the privacy/cookie terms change materially: a stored decision from
 * an older version is treated as "no decision", so the banner reappears and the
 * visitor re-consents to the new terms.
 */
export const CONSENT_VERSION = 1;

/** localStorage key holding the analytics consent decision. */
export const CONSENT_STORAGE_KEY = 'seuid_consent_analytics';

export type ConsentStatus = 'granted' | 'denied';

interface StoredConsent {
  status: ConsentStatus;
  /** epoch millis of the decision */
  ts: number;
  /** policy version the decision was made against */
  v: number;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

// ── Module-level singleton state ────────────────────────────────────────────
// Shared across every call site so the banner, the footer/privacy "Cookies"
// link and any test all observe the same reactive decision.

const status = ref<ConsentStatus | null>(null);
const bannerVisible = ref(false);
let gtagLoaded = false;
let initialized = false;

// ── Persistence helpers ─────────────────────────────────────────────────────

function readStored(): StoredConsent | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    if (parsed?.status !== 'granted' && parsed?.status !== 'denied') return null;
    if (parsed.v !== CONSENT_VERSION) return null; // stale policy → re-ask
    return { status: parsed.status, ts: Number(parsed.ts) || 0, v: parsed.v };
  } catch {
    return null;
  }
}

function writeStored(next: ConsentStatus): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const payload: StoredConsent = { status: next, ts: Date.now(), v: CONSENT_VERSION };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* private-mode / quota — the decision still holds for this session */
  }
}

// ── GA config from the server bootstrap ─────────────────────────────────────

/**
 * The configured GA4 measurement id (e.g. "G-XXXXXXXXXX"), or null when the
 * operator has not set GA_MEASUREMENT_ID. Read from the bootstrap snapshot so
 * it works before Pinia is installed. Not a secret: it ships in the page HTML
 * of every GA-enabled deployment.
 */
export function gaMeasurementId(): string | null {
  const ui = getBootstrapValue('ui');
  const id = ui?.analytics?.ga_measurement_id;
  return typeof id === 'string' && id.trim().length > 0 ? id.trim() : null;
}

/** Whether analytics is available at all (i.e. an operator configured it). */
export function analyticsConfigured(): boolean {
  return gaMeasurementId() !== null;
}

/**
 * The per-response CSP nonce to stamp on the runtime-injected gtag.js so it
 * satisfies the strict `script-src 'nonce-…'` policy WITHOUT widening script-src.
 *
 * Read from a LIVE server-rendered <script> element via its `.nonce` IDL
 * property. Browsers blank the `nonce` *content* attribute after parse
 * (nonce-hiding, to stop exfiltration via CSS selectors) but preserve the value
 * on the IDL property, so `script.nonce` is the authoritative document nonce and
 * always matches the active CSP header. We deliberately do NOT trust the
 * bootstrap payload's `nonce` field here: that snapshot can be overwritten by a
 * later client-side `/bootstrap/me` refresh whose nonce belongs to a *different*
 * response and would be rejected by this page's CSP. The bootstrap value is only
 * a last-resort fallback (e.g. SSR/tests with no nonce'd script in the DOM).
 */
function cspNonce(): string | null {
  if (typeof document !== 'undefined') {
    const scripts = document.querySelectorAll<HTMLScriptElement>('script');
    for (const script of scripts) {
      if (script.nonce) return script.nonce;
    }
  }
  const nonce = getBootstrapValue('nonce');
  return typeof nonce === 'string' && nonce.length > 0 ? nonce : null;
}

// ── gtag injection ──────────────────────────────────────────────────────────

/**
 * Inject gtag.js at runtime and initialize GA4 with IP anonymization. Called
 * ONLY after consent is granted. Idempotent: a second call is a no-op.
 */
function loadGtag(): void {
  if (gtagLoaded) return;
  const measurementId = gaMeasurementId();
  if (!measurementId || typeof document === 'undefined') return;

  gtagLoaded = true;

  const nonce = cspNonce();
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  // Stamp the CSP nonce so the strict script-src accepts this tag without
  // widening script-src. In dev CSP also allows 'unsafe-inline', but we still
  // stamp the nonce so prod and dev behave identically.
  if (nonce) script.setAttribute('nonce', nonce);
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // Canonical gtag shim: pushes the raw `arguments` to the dataLayer.
  window.gtag = function gtag(): void {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };
  window.gtag('js', new Date());
  // Consent Mode: analytics storage is granted (we only reach here on accept).
  window.gtag('consent', 'update', { analytics_storage: 'granted' });
  window.gtag('config', measurementId, { anonymize_ip: true });
}

// ── Public composable ───────────────────────────────────────────────────────

/**
 * Consent state + actions. Safe to call from anywhere; state is a shared
 * singleton. Call `init()` once (App.vue) to resolve the stored decision and,
 * if already granted, re-load gtag for this page view.
 */
export function useCookieConsent() {
  /**
   * Resolve the persisted decision and reconcile side-effects:
   *  - no analytics configured → never show the banner, never load anything.
   *  - stored 'granted'         → re-load gtag for this page view.
   *  - stored 'denied'          → load nothing.
   *  - no decision yet          → show the banner.
   */
  function init(): void {
    if (initialized) return;
    initialized = true;

    if (!analyticsConfigured()) {
      status.value = null;
      bannerVisible.value = false;
      return;
    }

    const stored = readStored();
    if (!stored) {
      status.value = null;
      bannerVisible.value = true;
      return;
    }

    status.value = stored.status;
    bannerVisible.value = false;
    if (stored.status === 'granted') loadGtag();
  }

  /** Grant consent: persist, hide the banner, and load GA. */
  function accept(): void {
    status.value = 'granted';
    bannerVisible.value = false;
    writeStored('granted');
    loadGtag();
  }

  /** Refuse consent: persist, hide the banner, load nothing. */
  function reject(): void {
    status.value = 'denied';
    bannerVisible.value = false;
    writeStored('denied');
  }

  /**
   * Reopen the banner so the visitor can change (revoke) a prior choice. Clears
   * the stored decision. If GA was already loaded this page view, a full reload
   * is required to actually stop it collecting — callers on the privacy page do
   * that after the next decision; for now we simply re-ask.
   */
  function openBanner(): void {
    if (!analyticsConfigured()) return;
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.removeItem(CONSENT_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
    status.value = null;
    bannerVisible.value = true;
  }

  return {
    // state
    status: readonly(status),
    isVisible: computed(() => bannerVisible.value && analyticsConfigured()),
    analyticsConfigured: computed(() => analyticsConfigured()),
    // actions
    init,
    accept,
    reject,
    openBanner,
  };
}

/**
 * Test-only reset of the module singleton. Not exported from a barrel; imported
 * directly by specs.
 * @internal
 */
export function _resetCookieConsentForTesting(): void {
  status.value = null;
  bannerVisible.value = false;
  gtagLoaded = false;
  initialized = false;
}
